import _objectWithoutPropertiesLoose from "@babel/runtime/helpers/esm/objectWithoutPropertiesLoose";
const _excluded = ["data"];
import { ApolloProvider } from '@apollo/client';
import { DataFetchingClient, DataFetchingClientProvider } from 'data-fetching-client';
import isEmpty from 'hs-lodash/isEmpty';
import isEqual from 'hs-lodash/isEqual';
import noop from 'hs-lodash/noop';
import { createApolloClient } from 'hubspot-apollo-client';
import { useCallback } from 'react';
import { QUERY_CLIENTS } from '../constants/query-clients';
import { jsx as _jsx } from "react/jsx-runtime";
const getVariableMatcher = variables => isEmpty(variables) ? obsVars => isEmpty(obsVars) : obsVars => isEqual(obsVars, variables);
export const createQueryDataProvider = (Context, queryCache, dataFetchingClientOptions) => {
  const DFC = new DataFetchingClient(dataFetchingClientOptions);
  const defaultApolloClient = createApolloClient({
    connectToDevTools: true
  });
  function getObservableQuery(client, query, variables) {
    const observables = client.getObservableQueries('active');
    const transformedQuery = client.documentTransform.transformDocument(query);
    const checkVariablesMatch = getVariableMatcher(variables);
    for (const observable of observables.values()) {
      if (
      // we check is empty because the observable will provide `{}` as its variables
      // which, if `variables` is `undefined`, will not match
      isEqual(transformedQuery, observable.query) && checkVariablesMatch(observable.variables)) {
        return observable;
      }
    }
    return undefined;
  }
  function createObservableQuery(client, query, variables) {
    // if we get here, there isn't a query that matches with the same variables
    // so we need to create one, but this code should only hit if the call for data
    // is done in a lower component or hook than when a consumer calls `subscribe`
    // In those cases, the subscription and the data fetching subscription will not
    // use the same observer
    return client.watchQuery({
      query,
      variables
    });
  }
  function getOrCreateObservableQuery(client, query, variables) {
    return getObservableQuery(client, query, variables) || createObservableQuery(client, query, variables);
  }
  const checkIsDFC = clientType => clientType === QUERY_CLIENTS.DFC;
  const extractDataResult = _ref => {
    let {
        data
      } = _ref,
      result = _objectWithoutPropertiesLoose(_ref, _excluded);
    return Object.assign({}, result, {
      data: data === null || data === void 0 ? void 0 : data.result
    });
  };
  function wrapObserver(clientType, observer) {
    if (checkIsDFC(clientType)) {
      return result => {
        observer(extractDataResult(result));
      };
    }
    return observer;
  }
  return ({
    children,
    client: maybeDFClient,
    apolloClient: maybeApolloClient
  }) => {
    const apolloClient = maybeApolloClient || defaultApolloClient;
    const dfClient = maybeDFClient || DFC;
    const getQueryClient = useCallback(clientType => {
      switch (clientType) {
        case 'apollo':
          return apolloClient;
        case 'data-fetching-client':
        default:
          return dfClient;
      }
    }, [apolloClient, dfClient]);
    const getCachedQueryState = useCallback(({
      clientType,
      fieldName,
      variables
    }) => {
      const query = queryCache.getAnyCachedQuery(fieldName);

      // NOTE: there is a non-null assertion on `query` here because `getAnyCachedQuery`
      // will throw an error if it cannot find a query/mutation for the provided `fieldName`
      const observable = getObservableQuery(getQueryClient(clientType), query, variables);
      const result = observable === null || observable === void 0 ? void 0 : observable.getCurrentResult();
      if (!result) {
        return result;
      }
      if (checkIsDFC(clientType)) {
        return extractDataResult(result);
      }
      return result;
    }, [getQueryClient]);
    const getRefetch = useCallback(({
      fieldName,
      variables,
      clientType
    }) => {
      const query = queryCache.getAnyCachedQuery(fieldName);
      const client = getQueryClient(clientType);

      // NOTE: there is a non-null assertion on `query` here because `getAnyCachedQuery`
      // will throw an error if it cannot find a query/mutation for the provided `fieldName`
      return refetchVariables => getOrCreateObservableQuery(client, query, variables).refetch(refetchVariables);
    }, [getQueryClient]);
    const subscribe = useCallback(({
      clientType,
      fieldName,
      observer,
      onError = noop,
      onResubscribe = noop,
      variables
    }) => {
      const client = getQueryClient(clientType);
      const query = queryCache.getAnyCachedQuery(fieldName);
      if (!query) {
        throw new Error('Attempted to get an observable for a known query that has not been cached');
      }

      // we explicitly set the observable type here because we know it will be for a DFC query
      const observable = getOrCreateObservableQuery(client, query, variables);
      const wrappedObserver = wrapObserver(clientType, observer);

      /**
       * The `resubscribeAfterError` approach is the same thing that `useQuery` does
       * for its error-handling:
       *
       * https://github.com/apollographql/apollo-client/blob/9d6c30697859724d9af532956a3b52ba256def89/src/react/hooks/useQuery.ts#L419-L423
       *
       * Without doing this, the main query execution will happen but consumers of `useQueryData` will not be notified of the
       * error change
       */
      const onErrorInternal = error => {
        /* eslint-disable @typescript-eslint/no-use-before-define */
        subscription.unsubscribe();
        subscription = observable.resubscribeAfterError(wrappedObserver, onErrorInternal);
        onResubscribe(subscription);
        /* eslint-enable @typescript-eslint/no-use-before-define */

        onError(error);
      };
      let subscription = observable.subscribe(wrappedObserver, onErrorInternal);
      return subscription;
    }, [getQueryClient]);
    return /*#__PURE__*/_jsx(ApolloProvider, {
      client: apolloClient,
      children: /*#__PURE__*/_jsx(DataFetchingClientProvider, {
        client: dfClient,
        children: /*#__PURE__*/_jsx(Context.Provider, {
          value: {
            getCachedQueryState,
            getRefetch,
            subscribe
          },
          children: children
        })
      })
    });
  };
};